import { ReactNode, useRef, useState } from 'react'
import {
    arrow,
    flip,
    FloatingArrow,
    FloatingPortal,
    offset,
    safePolygon,
    shift,
    useDismiss,
    useFloating,
    UseFloatingOptions,
    useHover,
    useInteractions,
    useRole,
    useTransitionStyles,
} from '@floating-ui/react'
import { classNames } from '@/Utils/Utils'

function Tippy({
    content,
    placement = 'top',
    disabled = false,
    keepOpenOnHover = false,
    children,
    className,
    size = 'md',
}: {
    content: ReactNode | string | null
    placement?: UseFloatingOptions['placement']
    disabled?: boolean
    keepOpenOnHover?: boolean
    children: ReactNode
    className?: string
    size?: 'sm' | 'md' | 'lg'
}) {
    const [open, setOpen] = useState(false)
    const arrowRef = useRef(null)

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement,
        middleware: [
            offset(6),
            flip(),
            shift(),
            arrow({
                element: arrowRef,
            }),
        ],
    })

    const { isMounted, styles } = useTransitionStyles(context, {
        duration: 200,
    })

    const hover = useHover(context, {
        delay: { open: 100, close: 50 },
        enabled: !disabled,
        // When enabled, keep open while hovering reference or floating element
        handleClose: keepOpenOnHover ? safePolygon({ buffer: 5 }) : undefined,
    })
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'tooltip' })

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role])

    if (!content) {
        return <>{children}</>
    }

    const sizeClass = size === 'sm' ? 'max-w-60' : size === 'lg' ? 'max-w-120' : 'max-w-80'

    return (
        <>
            <span ref={refs.setReference} {...getReferenceProps()} className={className}>
                {children}
            </span>

            {isMounted && open && !disabled && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={{
                            ...floatingStyles,
                            ...styles,
                        }}
                        {...getFloatingProps()}
                        className={classNames(
                            'z-100 rounded bg-stone-800 px-2 py-1 text-sm wrap-anywhere whitespace-pre-wrap text-white shadow',
                            sizeClass,
                            !keepOpenOnHover ? 'pointer-events-none' : ''
                        )}
                    >
                        {typeof content === 'string' ? <span dangerouslySetInnerHTML={{ __html: content }} /> : content}
                        <FloatingArrow ref={arrowRef} context={context} className={'fill-stone-800'} />
                    </div>
                </FloatingPortal>
            )}
        </>
    )
}

export default Tippy
